import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ElementRef, Injectable, Renderer2, RendererFactory2, ViewChild } from '@angular/core';
import { MatDialog } from "@angular/material/dialog";
import { defer, NEVER } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SpinnerOverlayComponent } from '../components/spinner-overlay/spinner-overlay.component';
import { UmpLoadingOverlayComponent } from '../components/usermngportal/ump-loading-overlay/ump-loading-overlay.component';

export type SpinnerOverlayType = 'spinner' | 'ump-loading-overlay';

@Injectable({
    providedIn: 'root',
})
export class SpinnerOverlayService {
    private overlayRef: OverlayRef | undefined = undefined;
    @ViewChild('main-content', { static: true, read: ElementRef })
    private divElementRef: ElementRef | undefined;
    private readonly renderer: Renderer2;
    public skipInterceptor = false;

    // Shared lifecycle across every caller: the overlay is only created when the first
    // subscriber starts and only removed when the last active subscriber finishes.
    // This avoids concurrent callers (e.g. the HTTP interceptor and a component) from
    // overwriting/detaching each other's overlayRef.
    private activeCount = 0;

    constructor(
        private readonly overlay: Overlay,
        private readonly dialogForm: MatDialog,
        rendererFactory: RendererFactory2
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    public spinner$(overlayType: SpinnerOverlayType = 'spinner') {
        return defer(() => {
            this.show(overlayType);
            return NEVER.pipe(
                finalize(() => {
                    this.hide();
                })
            );
        });
    }


    setTrueskipInterceptor() {
        this.skipInterceptor = true;
    }
    setFalseskipInterceptor() {
        this.skipInterceptor = false;
    }

    private show(overlayType: SpinnerOverlayType = 'spinner'): void {
        this.activeCount++;
        if (this.activeCount > 1) {
            // An overlay is already active for another subscriber; just share it.
            return;
        }

        const portal = overlayType === 'ump-loading-overlay'
            ? new ComponentPortal(UmpLoadingOverlayComponent)
            : new ComponentPortal(SpinnerOverlayComponent);

        this.divElementRef = new ElementRef(document.getElementById("main-content"))
        if (this.dialogForm.openDialogs.length === 0 && this.divElementRef.nativeElement !== null) {
            //this.renderer.setStyle(document.getElementById("main-content") as HTMLElement, 'transform', 'translateZ(0)');
            Promise.resolve(null).then(() => {
                if (this.activeCount <= 0) {
                    // All subscribers finished before this microtask ran; nothing to show.
                    return;
                }
                this.overlayRef = this.overlay.create({
                    positionStrategy: this.overlay
                        .position()
                        .flexibleConnectedTo(this.divElementRef ?? new ElementRef(''))
                        .withPositions([
                            {
                                originX: 'center',
                                originY: 'center',
                                overlayX: 'center',
                                overlayY: 'center'
                            }
                        ]) ,                               
                    backdropClass: 'backdropClass',
                    panelClass: 'backdropClass'
                });                           
                this.overlayRef.attach(portal);
            });
        }
        else {
            Promise.resolve(null).then(() => {
                if (this.activeCount <= 0) {
                    // All subscribers finished before this microtask ran; nothing to show.
                    return;
                }
                this.overlayRef = this.overlay.create({
                    positionStrategy: this.overlay
                        .position()
                        .global()
                        .centerHorizontally()
                        .centerVertically(),
                    hasBackdrop: true,
                });
                this.overlayRef.attach(portal);
            })

        }
    }

    public hide(): void {
        if (this.activeCount > 0) {
            this.activeCount--;
        }
        if (this.activeCount > 0) {
            // Other subscribers are still active; keep the overlay visible.
            return;
        }
        this.overlayRef?.detach();
        this.overlayRef = undefined;
    }
}